/**
 * 从 ZIP ArrayBuffer 中提取 .dfu 条目（仅支持 STORED=0 与 DEFLATE=8）。
 * 使用 DecompressionStream('deflate-raw')，不依赖 eval / Worker / 第三方解压库，便于严格 CSP 环境。
 */
/* global DecompressionStream */

function readU16(u8, o) {
  return u8[o] | (u8[o + 1] << 8);
}

function readU32(u8, o) {
  return (
    (u8[o] | (u8[o + 1] << 8) | (u8[o + 2] << 16) | (u8[o + 3] << 24)) >>> 0
  );
}

function findEOCD(u8) {
  const lo = 0x06054b50;
  const min = Math.max(0, u8.length - (65535 + 22));
  for (let e = u8.length - 22; e >= min; e--) {
    if (readU32(u8, e) === lo) {
      const cl = readU16(u8, e + 20);
      if (e + 22 + cl === u8.length) {
        return e;
      }
    }
  }
  for (let e = u8.length - 22; e >= 0; e--) {
    if (readU32(u8, e) === lo) {
      return e;
    }
  }
  throw new Error("无效的 ZIP：未找到中央目录结束标记");
}

async function inflateRawDeflate(compressedU8) {
  if (typeof DecompressionStream === "undefined") {
    throw new Error(
      "当前浏览器不支持 DecompressionStream，无法解压 DEFLATE 条目"
    );
  }
  let ds;
  try {
    ds = new DecompressionStream("deflate-raw");
  } catch (e) {
    throw new Error("不支持 deflate-raw 解压: " + e);
  }
  const stream = new Blob([compressedU8]).stream().pipeThrough(ds);
  const out = await new Response(stream).arrayBuffer();
  return new Uint8Array(out);
}

function utf8Decode(u8) {
  if (typeof TextDecoder !== "undefined") {
    return new TextDecoder("utf-8", { fatal: false }).decode(u8);
  }
  let s = "";
  for (let i = 0; i < u8.length; i++) {
    s += String.fromCharCode(u8[i]);
  }
  return s;
}

function localDataOffset(u8, localOff) {
  if (readU32(u8, localOff) !== 0x04034b50) {
    throw new Error("本地文件头签名错误");
  }
  const n = readU16(u8, localOff + 26);
  const m = readU16(u8, localOff + 28);
  return localOff + 30 + n + m;
}

function toExactArrayBuffer(u8) {
  if (u8.byteOffset === 0 && u8.byteLength === u8.buffer.byteLength) {
    return u8.buffer;
  }
  return u8.slice().buffer;
}

async function decodeEntryData(u8, entry) {
  const dataStart = localDataOffset(u8, entry.localOff);
  const localMethod = readU16(u8, entry.localOff + 8);
  if (localMethod !== entry.method) {
    throw new Error("ZIP 头不一致: " + entry.name);
  }
  if (dataStart + entry.compSize > u8.length) {
    throw new Error("ZIP 数据越界: " + entry.name);
  }

  const compSlice = u8.subarray(dataStart, dataStart + entry.compSize);
  let outU8;
  if (entry.method === 0) {
    outU8 = compSlice;
  } else if (entry.method === 8) {
    outU8 = await inflateRawDeflate(compSlice);
  } else {
    throw new Error(
      "不支持的压缩方式 " + entry.method + "（文件 " + entry.name + "）"
    );
  }

  if (
    entry.uncompSize !== 0 &&
    entry.uncompSize !== 0xffffffff &&
    outU8.length !== entry.uncompSize
  ) {
    throw new Error("解压后长度不匹配: " + entry.name);
  }
  return outU8;
}

function parseCentralDirectoryEntries(u8) {
  const eocd = findEOCD(u8);
  const cdSize = readU32(u8, eocd + 12);
  const cdOffset = readU32(u8, eocd + 16);
  const end = cdOffset + cdSize;
  let pos = cdOffset;
  const entries = [];

  while (pos < end) {
    if (readU32(u8, pos) !== 0x02014b50) {
      break;
    }
    const method = readU16(u8, pos + 10);
    const compSize = readU32(u8, pos + 20);
    const uncompSize = readU32(u8, pos + 24);
    const nameLen = readU16(u8, pos + 28);
    const extraLen = readU16(u8, pos + 30);
    const commentLen = readU16(u8, pos + 32);
    const localOff = readU32(u8, pos + 42);
    const nameBytes = u8.subarray(pos + 46, pos + 46 + nameLen);
    const name = utf8Decode(nameBytes).replace(/\\/g, "/");
    const headerSize = 46 + nameLen + extraLen + commentLen;

    entries.push({
      name: name,
      method: method,
      compSize: compSize,
      uncompSize: uncompSize,
      localOff: localOff,
      isDirectory: name.endsWith("/"),
    });

    pos += headerSize;
  }
  return entries;
}

async function collectDfuEntriesFromZip(
  arrayBuffer,
  pathPrefix,
  depth,
  maxDepth
) {
  if (depth > maxDepth) {
    throw new Error("ZIP 嵌套层级过深（>" + maxDepth + "）");
  }

  const u8 = new Uint8Array(arrayBuffer);
  const entries = parseCentralDirectoryEntries(u8);
  const out = [];

  for (const entry of entries) {
    if (entry.isDirectory) {
      continue;
    }

    const lowerName = entry.name.toLowerCase();
    if (!lowerName.endsWith(".dfu") && !lowerName.endsWith(".zip")) {
      continue;
    }

    const entryData = await decodeEntryData(u8, entry);
    if (lowerName.endsWith(".dfu")) {
      out.push({
        name: pathPrefix + entry.name,
        data: toExactArrayBuffer(entryData),
      });
      continue;
    }

    const nestedPrefix = pathPrefix + entry.name + "/";
    const nestedBuffer = toExactArrayBuffer(entryData);
    const nestedDfu = await collectDfuEntriesFromZip(
      nestedBuffer,
      nestedPrefix,
      depth + 1,
      maxDepth
    );
    out.push(...nestedDfu);
  }

  return out;
}

/**
 * @param {ArrayBuffer} arrayBuffer
 * @returns {Promise<Array<{ name: string, data: ArrayBuffer }>>}
 */
export async function unzipDfuEntries(arrayBuffer) {
  return collectDfuEntriesFromZip(arrayBuffer, "", 0, 3);
}
